import {Logger, NumberUtils} from 'neo-common';
import {AutoplayData, Balance, Cashier, FeatureData, GameDataLoaded, Help, History, IPlatforms, Paytable, RoundEnded, RoundStarted, SpinReceived} from './IPlatforms';
import {Platforms} from './Platforms';
import {AppContextBase} from '../GameEngine';

export class PlatformPlaytech implements IPlatforms {

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    //mapping received platform to game messages to function handler
    protected w2gMappings: Object;

    protected supportedFeatures: Array<string>;

    //if true, wait for gameround to be ended, then freeze game
    protected freezeGameWhenIdle: boolean;

    private initialized: boolean;

    //handle first call of loadProgress
    private loadStarted: boolean;

    //store current balance to calculate win
    private currentBalance: number;

    private gameRoundClosed: boolean;

    private openedDialogs: Array<string>;

    private activeAutoplay: boolean;

    private restoreAutoplay: boolean;

    private autoplayData: AutoplayData;

    private winValue: number;

    private currentBet: number;

    private closeGameRequest: boolean;

    private inSpin: boolean;

    private inFeature: boolean;

    private inPause: boolean;

    private _configuration: any;

    private _balanceData: Balance;

    private _eventStack: Array<{[id: string]: Object}>;

    //******************************************************************************************************************
    //  INIT
    //******************************************************************************************************************

    public create(): void {
        Platforms.canPlay = true;

        this.freezeGameWhenIdle = false;
        this.initialized = false;
        this.loadStarted = false;
        this.currentBalance = -1;
        this.gameRoundClosed = true;
        this.openedDialogs = [];
        this.activeAutoplay = false;
        this.restoreAutoplay = false;
        this.winValue = 0;
        this.closeGameRequest = false;
        this.inSpin = false;
        this.inFeature = false;
        this.inPause = false;
        this._eventStack = [];

        let context = AppContextBase.getInstance();
        if (context != null) {
            this._configuration = context.gameConfiguration;
        }

        if (DEBUG) {
            this.log('create');
        }

        this.createPlugin();
    }

    protected createPlugin(): void {
        if (DEBUG) {
            this.log('createPlugin');
        }

        this.supportedFeatures = [];
        this.w2gMappings = {
            'ucip.basic.w2gInitializationResponse': this.initializeResponseHandler.bind(this),
            'ucip.autoplay.w2gInterruptGameplayCommand': this.interruptGameplayHandler.bind(this),
            'ucip.pause.w2gPauseCommand': this.gamePauseHandler.bind(this),
            'ucip.displaystatus.w2gVisibilityChangeNotification': this.visibilityChangeHandler.bind(this),
            'ucip.sound.w2gSoundUpdateNotification': this.soundUpdateHandler.bind(this),
            'ucip.balancerefresh.w2gRefreshBalanceCommand': this.refreshBalanceHandler.bind(this),
            'ucip.closegame.w2gCloseGameRequest': this.closeGameHandler.bind(this)
        };

        window.addEventListener('message', this.handleMessageReceived.bind(this));

        //send init request to platform
        this.initRequest();
    }

    //******************************************************************************************************************
    //  COMUNICATION
    //******************************************************************************************************************

    /**
     * Handler for message listener to receive messages from platform.
     * @param event - message event with data from platform
     */
    protected handleMessageReceived(event: MessageEvent): void {
        if (event && event.data != null) {
            let jsonObj = JSON.parse(event.data);

            if (jsonObj != null && jsonObj._type != null) {
                if (this.w2gMappings && this.w2gMappings[jsonObj._type] != null) {
                    this.w2gMappings[jsonObj._type](jsonObj);
                } else {
                    if (DEBUG) {
                        this.log('[GAME ERROR]: Undefined request');
                    }
                }
            }
        }
    }

    /**
     * Handler to send data from game to platform.
     * @param data - data object send back to platform
     * @param init - init request
     */
    protected handleMessageSend(data: Object, init: boolean = false): void {
        if (!init && !this.initialized) {
            return;
        }

        if (data != null) {
            let requestString = JSON.stringify(data);
            window.parent.postMessage(requestString, '*');
        }
    }

    //******************************************************************************************************************
    //  GAME TO PLATFORM EVENTS
    //******************************************************************************************************************

    public initRequest(): void {
        if (DEBUG) {
            this.log('initRequest - g2wInitializationRequest');
        }

        let features = ['pause', 'autoplay', 'closegame', 'sound', 'openpage', 'switchgame', 'gamechange', 'loadingstatus', 'errors', 'gameround', 'balanceupdate', 'balancerefresh', 'betupdate', 'displaystatus'];

        if (this._configuration != null) {
            if (this._configuration['ShowSimilarGames'] === false || this._configuration['StaticContentUrl'] === '' || this._configuration['SimilarGamesList'].length === 0) {
                features.splice(features.indexOf('switchgame'), 1);
                features.splice(features.indexOf('gamechange'), 1);
            }
        }

        const data = {
            _type: 'ucip.basic.g2wInitializationRequest',
            version: '1.0.0',
            features: features
        };

        this.handleMessageSend(data, true);
    }

    public autoplayFinished(args?: AutoplayData): void {
        if (DEBUG) {
            this.log('autoplayFinished - g2wAutoplayEndNotification');
        }

        this.activeAutoplay = false;
        this.autoplayData = args;

        if (this.winValue > 0 && this.restoreAutoplay) {
            const count = parseInt(this.autoplayData.autoplayCount);
            if (!isNaN(count)) {
                this.autoplayData.autoplayCount = (count + 1).toString();
            }
        }

        const data = {
            _type: 'ucip.autoplay.g2wAutoplayEndNotification'
        };

        if (!this.isSupported('autoplay')) {
            return;
        }

        this.handleMessageSend(data);
    }

    public autoplayStarted(): void {
        if (DEBUG) {
            this.log('autoplayStarted - g2wAutoplayStartNotification');
        }

        this.activeAutoplay = true;
        const data = {
            _type: 'ucip.autoplay.g2wAutoplayStartNotification'
        };

        if (!this.isSupported('autoplay')) {
            return;
        }

        this.handleMessageSend(data);
    }

    public balanceUpdated(args?: Balance): void {
        if (DEBUG) {
            this.log('balanceUpdated - g2wBalanceUpdateNotification');
        }

        this._balanceData = args;

        if (this.inPause) {
            return;
        }

        if (args != null) {
            // let win = this.winValue;
            let win = parseFloat(NumberUtils.subtract(args.amount, this.currentBalance));
            if (win < 0) {
                win = 0;
            }

            //balance is updated by bet, so we need to add this value to win
            if (this.inSpin && win > 0) {
                win = parseFloat(NumberUtils.add(win, this.currentBet));
            }

            const data = {
                _type: 'ucip.balanceupdate.g2wBalanceUpdateNotification',
                balance: {
                    value: args.amount,
                    winAmount: args.forced === true ? 0 : win
                }
            };

            if (this.currentBalance === args.amount) {
                return;
            }

            this.handleMessageSend(data);
            this.currentBalance = args.amount;
        }
    }

    public bet(value: number): void {
        if (DEBUG) {
            this.log('bet - g2wBetUpdateNotification');
        }

        const data = {
            _type: 'ucip.betupdate.g2wBetUpdateNotification',
            totalBet: value
        };

        this.currentBet = value;

        if (this.isSupported('betupdate')) {
            this.handleMessageSend(data);
        } else {
            if (!this.initialized) {
                this.addToStack('betupdate', data);
            }
        }
    }

    public error(code: string, message: string): void {
        if (DEBUG) {
            this.log('error - g2wErrorNotification');
        }

        if (!this.isSupported('errors')) {
            return;
        }

        let httpStatus = code.split('-');
        let type = 'GENERIC_ERROR';

        if (httpStatus.length > 0) {
            switch (httpStatus[0]) {
                case '0':
                    type = 'CONNECTION_ERROR';
                    break;
                case '401':
                    type = 'AUTHENTICATION_ERROR';
                    break;
                case '500':
                    if (httpStatus.length === 2 && httpStatus[1] === '2005') {
                        type = 'INSUFFICIENT_FUNDS';
                    }
                    break;
            }
        }

        const data = {
            _type: 'ucip.errors.g2wErrorNotification',
            error: {
                type: type,
                description: message
            }
        };

        this.handleMessageSend(data);
    }

    public clientError(id: string, message: string): void {
        if (DEBUG) {
            this.log('client error - g2wClientErrorNotification');
        }

        let type = 'GENERIC_ERROR';

        switch (id) {
            case 'NOT_ENOUGH_CREDIT':
                type = 'INSUFFICIENT_FUNDS';
                break;
        }

        const data = {
            _type: 'ucip.errors.g2wErrorNotification',
            error: {
                type: type,
                description: message
            }
        };

        this.handleMessageSend(data);
    }

    public featureFinished(args?: FeatureData): void {
        this.inFeature = false;
    }

    public featureStarted(args?: FeatureData): void {
        this.inFeature = true;
    }

    public featureInit(): void {
        this.inFeature = true;
    }

    public gameChange(gameCode: string): void {
        const command = Platforms.handleChangeGame ? 'ucip.switchgame.g2wSwitchGameCommand' : 'ucip.gamechange.g2wGameChangeNotification';
        if (DEBUG) {
            this.log('gameChange - ' + command.split('.')[2]);
        }

        const data = {
            _type: command,
            gameCode: gameCode
        };

        this.handleMessageSend(data);
    }

    public gameClosing(): void {
        if (DEBUG) {
            this.log('gameClosing - g2wCloseGameResponse');
        }

        let closeType = 'ucip.basic.g2wCloseGameFrameCommand';
        if (this.closeGameRequest) {
            closeType = 'ucip.closegame.g2wCloseGameResponse';
        }

        const data = {
            _type: closeType
        };

        this.handleMessageSend(data);
    }

    public gameReloading(): void {}

    public gameDataLoaded(args: GameDataLoaded): void {
        let data = {
            _type: 'ucip.loadingstatus.g2wGameReadyNotification'
        };

        if (this.isSupported('loadingstatus')) {
            this.handleMessageSend(data);
        } else {
            this.addToStack('loadingstatus', data);
        }

        if (args) {
            this.currentBalance = args.Balance;
            this.bet(args.Bet);
        }
    }

    public dialogOpened(type: string, isOpen: boolean): void {
        if (DEBUG) {
            this.log('dialogOpened - g2wUiStateNotification');
        }

        if (isOpen) {
            this.restoreAutoplay = false;
            this.openedDialogs.push(type);
        } else {
            const itemIndex = this.openedDialogs.indexOf(type);
            if (itemIndex === -1) {
                //trying to close dialog that is already closed
                return;
            }

            this.openedDialogs.splice(itemIndex, 1);

            if (this.openedDialogs.length > 0) {
                //wait until all dialogs are closed
                return;
            }
        }

        const data = {
            _type: 'ucip.displaystatus.g2wUiStateNotification',
            busy: isOpen
        };

        this.handleMessageSend(data);
    }

    public fullscreen(enabled: boolean): void {}

    public gameFailed(): void {
    }

    public gameReady(): void {
        if (DEBUG) {
            this.log('gameReady');
        }

        let data = {};

        data = {
            _type: 'ucip.loadingstatus.g2wLoadingProgressNotification',
            progress: 100
        };

        if (this.isSupported('loadingstatus')) {
            this.handleMessageSend(data);
        } else {
            this.addToStack('loadingstatus', data);
        }

        data = {
            _type: 'ucip.loadingstatus.g2wLoadingEndNotification'
        };

        if (this.isSupported('loadingstatus')) {
            this.handleMessageSend(data);
        } else {
            this.addToStack('loadingstatus', data);
        }
    }

    public gotoCasier(args?: Cashier): void {
        if (DEBUG) {
            this.log('gotoCasier - g2wOpenPageCommand');
        }

        if (args != null) {
            const data = {
                _type: 'ucip.openpage.g2wOpenPageCommand',
                pageID: 'cashier',
                url: args.target
            };

            this.handleMessageSend(data);
        }
    }

    public gotoHelp(args?: Help): void {
    }

    public gotoHistory(args?: History): void {
        if (DEBUG) {
            this.log('gotoHistory - g2wOpenPageCommand');
        }

        if (args != null) {
            const data = {
                _type: 'ucip.openpage.g2wOpenPageCommand',
                pageID: 'game_history',
                url: args.target
            };

            this.handleMessageSend(data);
        }
    }

    public gotoPaytable(args?: Paytable): void {
    }

    public loadProgress(progress: number): void {
        if (DEBUG) {
            this.log('loadProgress');
        }

        let initStart = false;
        let data = {};
        if (!this.loadStarted) {
            data = {
                _type: 'ucip.loadingstatus.g2wLoadingStartNotification'
            };
            initStart = true;
            this.loadStarted = true;
        } else {
            data = {
                _type: 'ucip.loadingstatus.g2wLoadingProgressNotification',
                progress: progress
            };
        }

        if (progress < 100) {
            if (this.isSupported('loadingstatus')) {
                this.handleMessageSend(data);
            } else if (initStart) {
                this.addToStack('loadingstatus', data);
            }
        }
    }

    public quickSpinEnabled(enabled: boolean): void {
    }

    public roundEnded(args?: RoundEnded): void {
        if (DEBUG) {
            this.log('roundEnded - g2wGameRoundEndNotification');
        }

        this.winValue = args ? args.win : 0;
        this.gameRoundClosed = true;
        let balanceNotificationSend = false;

        const data = {
            _type: 'ucip.gameround.g2wGameRoundEndNotification'
        };

        this.handleMessageSend(data);

        if (args && args.inSpin) {
            //send required balance updated with (balance - bet)
            const balanceWithWin = parseFloat(NumberUtils.add(this.currentBalance, args.win));
            const data = {
                _type: 'ucip.balanceupdate.g2wBalanceUpdateNotification',
                balance: {
                    value: balanceWithWin,
                    winAmount: args.win
                }
            };

            balanceNotificationSend = true;
            this.handleMessageSend(data);
            this.currentBalance = balanceWithWin;
        }

        if (this.freezeGameWhenIdle) {
            if (DEBUG) {
                this.log('gamePlayFreeze - g2wPauseNotification');
            }

            //we need to check, if there is any balance update because
            //after game is paused, balance update will be blocked
            const balanceWithWin = parseFloat(NumberUtils.add(this.currentBalance, args.win));
            const balanceData = {
                _type: 'ucip.balanceupdate.g2wBalanceUpdateNotification',
                balance: {
                    value: balanceWithWin,
                    winAmount: args.win
                }
            };

            if (balanceWithWin !== this.currentBalance && !balanceNotificationSend) {
                this.handleMessageSend(balanceData);
                this.currentBalance = balanceWithWin;
            }

            Platforms.gamePlayFreeze();

            const data = {
                _type: 'ucip.pause.g2wPauseNotification',
                pause: true
            };

            this.inPause = true;
            this.handleMessageSend(data);
        }
    }

    public roundInit(): void {}

    public roundStarted(args?: RoundStarted): void {
        if (DEBUG) {
            this.log('roundStarted - g2wGameRoundStartNotification');
        }

        this.winValue = 0;
        this.gameRoundClosed = false;
        const data = {
            _type: 'ucip.gameround.g2wGameRoundStartNotification'
        };

        this.handleMessageSend(data);
    }

    public sendPluginMessage(message: string): void {
    }

    public setVolume(level: number): void {
        if (DEBUG) {
            this.log('setVolume');
        }

        const data = {
            _type: 'ucip.sound.g2wSoundUpdateNotification',
            mute: level === 0,
            level: Math.round((level / 3) * 100)
        };

        this.handleMessageSend(data);
    }

    public spinEnded(): void {
        this.inSpin = false;
    }

    public spinReceived(args?: SpinReceived): void {
    }

    public spinStarted(): void {
        this.inSpin = true;
    }

    public userAction(action: string, data: Object): void {
    }

    public win(value: number, cumulative?: boolean): void {}

    //******************************************************************************************************************
    //  PLATFORM TO GAME
    //******************************************************************************************************************

    protected initializeResponseHandler(data: any): void {
        if (DEBUG) {
            this.log('initializeResponseHandler');
        }

        if (data == null || data['features'] == null) {
            return;
        }

        for (let i = 0; i < data['features'].length; i++) {
            const feature = data['features'][i];

            if (Array.isArray(feature) && feature.length === 2) {
                //first item should be name of feature, the second should contain extra data
                const featureName = feature[0];
                const featureData = feature[1];

                switch (featureName) {
                    case 'openpage':
                        const delegatePages = featureData['delegatePages'];
                        if (delegatePages != null && Array.isArray(delegatePages)) {
                            for (let j = 0; j < delegatePages.length; j++) {
                                if (delegatePages[j] === 'cashier') {
                                    Platforms.handleOpenCashierPage = true;
                                }
                                if (delegatePages[j] === 'history') {
                                    Platforms.handleOpenHistoryPage = true;
                                }
                            }
                        }
                        break;
                    case 'sound':
                        this.soundUpdateHandler(featureData);
                        break;
                }
                this.supportedFeatures.push(feature[0]);
            } else {
                switch (feature) {
                    case 'switchgame':
                        Platforms.handleChangeGame = true;
                        break;
                }

                this.supportedFeatures.push(feature);
            }
        }
        this.initialized = true;

        for (let i = 0; i < this._eventStack.length; i++) {
            const event = this._eventStack[i];

            for (let key in event) {
                if (!this.isSupported(key)) {
                    continue;
                }

                this.handleMessageSend(event[key]);
            }
        }
    }

    protected interruptGameplayHandler(data: any): void {
        if (DEBUG) {
            this.log('interruptGameplayHandler');
        }

        Platforms.stopAutoplay();
    }

    protected gamePauseHandler(data: any): void {
        if (DEBUG) {
            this.log('gamePauseHandler');
        }

        if (!this.isSupported('pause')) {
            return;
        }

        if (data.pause) {
            if (this.gameRoundClosed) {
                Platforms.gamePlayFreeze();

                const data = {
                    _type: 'ucip.pause.g2wPauseNotification',
                    pause: true
                };

                this.inPause = true;
                this.handleMessageSend(data);
            } else {
                //disable spin on uncollected win, then wait for round ended to emit gameplay freeze
                Platforms.gamePlaySupressSpinOnCollect();
                this.freezeGameWhenIdle = true;
                this.restoreAutoplay = this.activeAutoplay;
            }
        } else {
            Platforms.gamePlayUnfreeze();
            this.freezeGameWhenIdle = false;

            const data = {
                _type: 'ucip.pause.g2wPauseNotification',
                pause: false
            };
            this.handleMessageSend(data);

            if (this.restoreAutoplay) {
                Platforms.restoreAutoplay(this.autoplayData);
            }

            this.inPause = false;
            if (this.restoreAutoplay && this._balanceData != null) {
                this.balanceUpdated(this._balanceData);
            }

            this.restoreAutoplay = false;
        }
    }

    protected visibilityChangeHandler(data: any): void {
        if (DEBUG) {
            this.log('visibilityChangeHandler');
        }

        if (data == null) {
            return;
        }

        const visible = data['visible'];

        if (visible === true) {
            Platforms.gamePlayUnfreeze();
        } else {
            Platforms.gamePlayFreeze();
        }
    }

    protected soundUpdateHandler(data: any): void {
        if (DEBUG) {
            this.log('soundUpdateHandler');
        }

        if (data.mute) {
            Platforms.gameAudioMute();
        } else {
            Platforms.gameAudioSet(Math.round((data.level / 100) * 3));
        }
    }

    protected refreshBalanceHandler(data: any): void {
        if (DEBUG) {
            this.log('refreshBalanceHandler');
        }

        Platforms.refreshBalance();
    }

    protected closeGameHandler(data: any): void {
        if (DEBUG) {
            this.log('closeGameHandler');
        }

        if (this.inSpin || this.inFeature) {
            const data = {
                _type: 'ucip.closegame.g2wCloseGameErrorResponse'
            };

            this.handleMessageSend(data);
            return;
        }

        this.closeGameRequest = true;
        Platforms.closeGame();
    }

    //******************************************************************************************************************
    //  HELPER
    //******************************************************************************************************************

    protected isSupported(featureName: string): boolean {
        if (this.supportedFeatures.indexOf(featureName) === -1) {
            this.log(`Feature: ${featureName} is not supported`);
            return false;
        } else {
            return true;
        }
    }

    protected addToStack(featureName: string, data: any): void {
        const obj = {};
        obj[featureName] = data;

        this._eventStack.push(obj);
    }

    //******************************************************************************************************************
    //  LOG
    //******************************************************************************************************************

    protected log(s: string): void {
        if (DEBUG) {
            Logger.debugGroup('Playtech', 'Playtech ' + s);
        }
    }
}


/** WEBPACK FOOTER **
 ** ./Engine/Platforms/PlatformPlaytech.ts
 **/